import * as GC from '@grapecity/spread-sheets';
import { IVintageRunOffSchedule } from '@models/product-detail-report/vintage-run-off';
import { getDateString } from '@shared/dates';
import { borders, colours, CURRENCY_FORMATTER, FONT_NAME, FONT_SIZE, PERCENTAGE_FORMATTER } from '@shared/spread/spread-settings';

export const createRunOffScheduleExport = (
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  runOffSchedule: IVintageRunOffSchedule
) => {
  rowIndex = createHeaders(sheet, rowIndex, startCol);

  // Fixing Data Issues
  const startDate = new Date(runOffSchedule.AsOfDate);
  const allDates = runOffSchedule.VintageRunOffScheduleItems.map(({ PeriodEndDate }) => PeriodEndDate);
  const startIndex = allDates.findIndex(d => d.substr(0, 7) === startDate.toJSON().substr(0, 7));

  runOffSchedule.VintageRunOffScheduleItems = runOffSchedule.VintageRunOffScheduleItems.splice(
    startIndex,
    runOffSchedule.VintageRunOffScheduleItems.length - startIndex
  );

  addData(sheet, rowIndex, startCol, runOffSchedule);
  populateExtraInfo(sheet, rowIndex, startCol + 9, runOffSchedule);
};

function createHeaders(sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, startCol: number) {
  sheet.getCell(rowIndex, startCol).text('Period');
  sheet.getCell(rowIndex, startCol + 1).text('Date');
  sheet.getCell(rowIndex, startCol + 2).text('Stable Runoff');
  sheet.getCell(rowIndex, startCol + 3).text('Non-Stable Runoff');
  sheet.getCell(rowIndex, startCol + 4).text('Balance Runoff');
  sheet.getCell(rowIndex, startCol + 5).text('Prin Cashflow $');
  sheet.getCell(rowIndex, startCol + 6).text('Prin Cashflow %');

  // Applying Styling
  sheet
    .getRange(rowIndex, startCol, 1, 7)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .backColor(colours.LIGHT_GREY)
    .setBorder(borders.BLACK_MEDIUM, { all: true });

  rowIndex += 1;
  return rowIndex;
}

function addData(sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, startCol: number, runOffSchedule: IVintageRunOffSchedule) {
  // Adding Periods
  const periods = new Array(runOffSchedule.VintageRunOffScheduleItems.length).fill(0).map((val, ind) => ind);
  sheet.setArray(rowIndex, startCol, periods);

  // Adding Dates
  const dates = runOffSchedule.VintageRunOffScheduleItems.map(runOff => runOff.PeriodEndDate.substr(0, 10));
  sheet.setArray(rowIndex, startCol + 1, dates);

  // Adding Stable Run Off
  const stableBalanceRunOff = runOffSchedule.VintageRunOffScheduleItems.map(runOff => runOff.StableBalanceRunOff);
  sheet.setArray(rowIndex, startCol + 2, stableBalanceRunOff);

  // Adding non Stable Run Off
  const nonStableRunOff = runOffSchedule.VintageRunOffScheduleItems.map(runOff => runOff.NonStableBalanceRunOff);
  sheet.setArray(rowIndex, startCol + 3, nonStableRunOff);

  // Adding Balance Run Off
  const balanceRunOff = runOffSchedule.VintageRunOffScheduleItems.map(runOff => runOff.BalanceRunOff);
  sheet.setArray(rowIndex, startCol + 4, balanceRunOff);

  // Principle Cashflow
  const principleCashflow = runOffSchedule.VintageRunOffScheduleItems.map(runOff => runOff.PrinCashFlowSchedAmount);
  sheet.setArray(rowIndex, startCol + 5, principleCashflow);

  // Prin Cashflow %
  const principleCashflowPercent = runOffSchedule.VintageRunOffScheduleItems.map(runOff => runOff.PrinCashFlowSchedPercent);
  sheet.setArray(rowIndex, startCol + 6, principleCashflowPercent);

  // Applying Styling
  sheet
    .getRange(rowIndex, startCol, runOffSchedule.VintageRunOffScheduleItems.length, 7)
    .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
    .vAlign(GC.Spread.Sheets.VerticalAlign.center)
    .setBorder(borders.BLACK_MEDIUM, { outline: true });

  // Prin Cashflow % shouldn't be center aligned
  sheet.getRange(rowIndex, startCol + 6, sheet.getRowCount(), 1).hAlign(GC.Spread.Sheets.HorizontalAlign.right);

  // Formatters
  // 'Stable Run Off','Non Stable Run Off','Balance Run Off','Principle Cashflow Scheduled $'
  sheet.getRange(rowIndex, startCol + 2, runOffSchedule.VintageRunOffScheduleItems.length, 4).formatter(CURRENCY_FORMATTER);

  // Principle Cashflow Scheduled %
  sheet.getRange(rowIndex, startCol + 6, runOffSchedule.VintageRunOffScheduleItems.length, 1).formatter(PERCENTAGE_FORMATTER);
}

function populateExtraInfo(sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, startCol: number, vintageRunOff: IVintageRunOffSchedule) {
  // Extra info area
  sheet.getCell(rowIndex + 1, startCol).text('EOP Balance');
  sheet.getCell(rowIndex + 2, startCol).text('Avg. Balance');
  sheet.getCell(rowIndex + 3, startCol).text('WAL');
  sheet.getCell(rowIndex + 4, startCol).text('Stable Balance WAL');

  // Populating the As Of Date
  sheet.getCell(rowIndex, startCol + 1).value(getDateString(vintageRunOff.AsOfDate));

  // @ts-ignore - EOPBalance -> EopBalance
  sheet.getCell(rowIndex + 1, startCol + 1).value(vintageRunOff.EopBalance);
  sheet.getCell(rowIndex + 1, startCol + 1).formatter('###,###;[Red](###,###)');

  // Avg Balance
  sheet.getCell(rowIndex + 2, startCol + 1).value(vintageRunOff.AvgBalance);
  sheet.getCell(rowIndex + 2, startCol + 1).formatter('###,###;[Red](###,###)');

  // @ts-ignore - WAL -> Wal
  sheet.getCell(rowIndex + 3, startCol + 1).value(vintageRunOff.Wal);
  sheet.getCell(rowIndex + 3, startCol + 1).formatter('###,### 0.0000;[Red](###,### 0.0000)');

  sheet.getCell(rowIndex + 4, startCol + 1).value(vintageRunOff.StableBalanceWAL);
  sheet.getCell(rowIndex + 4, startCol + 1).formatter('###,### 0.0000;[Red](###,### 0.0000)');

  // Adding Styling
  sheet
    .getRange(rowIndex, startCol, 5, 2)
    .backColor(colours.LIGHT_BLUE)
    .cellPadding(`0 ${parseInt(FONT_SIZE, 10) / 2}`)
    .setBorder(borders.BLACK_MEDIUM, { outline: true });

  sheet
    .getRange(rowIndex, startCol, 5, 1)
    .font(`bold ${FONT_SIZE}pt ${FONT_NAME}`)
    .textDecoration(GC.Spread.Sheets.TextDecorationType.underline);
  sheet
    .getCell(rowIndex, startCol + 1)
    .font(`bold ${FONT_SIZE}pt ${FONT_NAME}`)
    .textDecoration(GC.Spread.Sheets.TextDecorationType.underline);

  sheet.setColumnWidth(startCol, 130);
  sheet.setColumnWidth(startCol + 1, 130);
}
